import React, { useEffect, useState } from "react";
import axios from "axios";
import { Stethoscope, Search, AlertTriangle, CheckCircle2, RefreshCw, Zap, History, Users } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../../utils/confirmDialog";
import DebtLedgerModal from "./DebtLedgerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

// "تدقيق حسابات الزبائن" — compares the customer_debt stored on each user
// against the value computed from their actual orders + receipts. Lets the
// admin click "تصحيح تلقائي" to re-sync the ledger.
//
// As of Feb 2026: added "إعادة حساب الكل" nuclear button that fixes every
// customer's debt in one click + per-customer ledger modal so admin can
// show customers exactly where their debt came from.
const DebtAuditTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [scanning, setScanning] = useState(false);
  const [recomputingAll, setRecomputingAll] = useState(false);
  const [ledgerCustomerId, setLedgerCustomerId] = useState(null);

  const scan = async () => {
    setScanning(true); setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customer-debt-audit`);
      setRows(data.customers || []);
      toast.success(`فحص اكتمل: ${data.count} زبون عنده خلل`);
    } catch { toast.error("فشل الفحص"); }
    finally { setScanning(false); setLoading(false); }
  };

  useEffect(() => { scan(); /* eslint-disable-next-line */ }, []);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.phone || "").includes(search.trim()) ||
      (r.market_name || "").toLowerCase().includes(q) ||
      (r.customer_id_code || "").toLowerCase().includes(q)
    );
  });

  const fix = async (row) => {
    const ok = await confirmDialog(
      `تصحيح حساب ${row.full_name || row.phone}؟\n\n` +
      `الدين المسجّل الآن: ${fmt(row.stored_debt)} د.ع\n` +
      `الدين الصحيح المحسوب: ${fmt(row.computed_debt)} د.ع\n` +
      `الفرق: ${row.difference > 0 ? "+" : ""}${fmt(row.difference)} د.ع\n\n` +
      `سيُعدَّل حساب الزبون لمطابقة الحساب الصحيح. الإجراء مسجّل بالسجل.`,
      { force: true, tone: "danger", title: "تأكيد التصحيح", confirmLabel: "نعم، صحّح" }
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/customer-debt-audit/${row.id}/fix`);
      toast.success(`تم تصحيح ${row.full_name || row.phone}`);
      scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التصحيح");
    }
  };

  const recomputeAll = async () => {
    const ok = await confirmDialog(
      `إعادة حساب ديون كل الزبائن من الصفر؟\n\n` +
      `النظام سيعيد حساب الدين لكل زبون باستخدام المعادلة:\n` +
      `الدين = مجموع الفواتير − مجموع سندات القبض + التعديلات اليدوية\n\n` +
      `هذه العملية آمنة 100% — لا تحذف أي بيانات.\n` +
      `كل التغييرات تُسجَّل في سجل الدين لكل زبون.`,
      { force: true, tone: "danger", title: "🛡 إعادة حساب جماعي", confirmLabel: "نعم، أعد حساب الكل" }
    );
    if (!ok) return;
    setRecomputingAll(true);
    try {
      const { data } = await axios.post(`${API}/admin/recompute-all-debts`);
      toast.success(
        `تم تصحيح ${data.fixed} من ${data.total_customers} زبون · فرق تراكمي ${fmt(data.drift_total)} د.ع`,
        { duration: 6000 }
      );
      scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إعادة الحساب");
    } finally {
      setRecomputingAll(false);
    }
  };

  return (
    <div className="space-y-4" dir="rtl">
      <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-[#1E3A5F]" />
            <h2 className="font-black text-lg text-[#1E3A5F]">تدقيق حسابات الزبائن</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={recomputeAll}
              disabled={recomputingAll || scanning}
              data-testid="recompute-all-debts-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-l from-[#DC2626] to-[#B91C1C] hover:from-[#B91C1C] hover:to-[#7F1D1D] disabled:opacity-50 text-white text-xs font-bold shadow-md"
              title="إعادة حساب ديون كل الزبائن من الصفر (آمن 100%)"
            >
              <Zap className={`w-3.5 h-3.5 ${recomputingAll ? "animate-pulse" : ""}`} />
              {recomputingAll ? "جاري الحساب..." : "🛡 إعادة حساب الكل"}
            </button>
            <button
              type="button"
              onClick={scan}
              disabled={scanning}
              data-testid="audit-rescan-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A5F] hover:bg-[#152a47] disabled:opacity-50 text-white text-xs font-bold"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} />
              {scanning ? "جاري الفحص..." : "إعادة الفحص"}
            </button>
          </div>
        </div>
        <p className="text-xs text-[#5C6E64] leading-relaxed">
          النظام يقارن الدين المسجّل بقاعدة البيانات مع الحساب الفعلي = (مجموع غير المسدّد من كل الفواتير − مجموع سندات القبض بمبلغها وخصمها + التعديلات اليدوية). الزبائن المعروضون هم اللي عندهم فرق ≥ 1 د.ع.
        </p>
        <div className="bg-gradient-to-l from-[#FEF3C7] to-[#FFFBEB] border border-[#F59E0B]/40 rounded-lg p-2 text-[11px] text-[#92400E] leading-relaxed">
          <strong>🛡 إعادة حساب الكل</strong>: زر الأمان النهائي — يعيد حساب ديون كل الزبائن من فواتيرهم وسنداتهم الفعلية. كل عملية مستقبلية (فاتورة، سند قبض، تعديل) تستخدم نفس المعادلة تلقائياً، فمن المستحيل يطلع دين خاطئ.
        </div>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث باسم الزبون، الهاتف، أو الماركت..."
            data-testid="audit-search"
            className="w-full pr-9 pl-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C6E64]">جاري الفحص...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#ECFDF5] border-2 border-[#15803D]/30 rounded-2xl p-6 text-center" data-testid="audit-empty">
          <CheckCircle2 className="w-12 h-12 text-[#15803D] mx-auto mb-2" />
          <p className="font-black text-[#15803D] text-lg">كل الحسابات سليمة!</p>
          <p className="text-sm text-[#5C6E64] mt-1">لا يوجد فرق بين الدين المسجّل والحساب الفعلي.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E4EBE6] overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-[#FEF3C7] text-[#92400E] text-xs">
              <tr>
                <th className="px-3 py-2 text-right">الزبون</th>
                <th className="px-3 py-2 text-center">الفواتير</th>
                <th className="px-3 py-2 text-center">سندات القبض</th>
                <th className="px-3 py-2 text-center">المسجّل</th>
                <th className="px-3 py-2 text-center">المحسوب</th>
                <th className="px-3 py-2 text-center">الفرق</th>
                <th className="px-3 py-2 text-center">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-[#E4EBE6] hover:bg-[#FFFBEB]" data-testid={`audit-row-${r.id}`}>
                  <td className="px-3 py-2 text-right">
                    <p className="font-bold text-[#1E3A5F]">{r.full_name || "—"}</p>
                    {r.market_name && <p className="text-xs text-[#5C6E64]">{r.market_name}</p>}
                    <p className="text-[10px] text-[#5C6E64]" dir="ltr">{r.phone}</p>
                  </td>
                  <td className="px-3 py-2 text-center text-[#5C6E64]">{r.orders_count}</td>
                  <td className="px-3 py-2 text-center text-[#5C6E64]" dir="ltr">{fmt(r.receipts_total)}</td>
                  <td className="px-3 py-2 text-center font-bold text-[#5C6E64]" dir="ltr">{fmt(r.stored_debt)}</td>
                  <td className="px-3 py-2 text-center font-bold text-[#1E3A5F]" dir="ltr">{fmt(r.computed_debt)}</td>
                  <td className="px-3 py-2 text-center font-black" dir="ltr" style={{ color: r.difference > 0 ? "#15803D" : "#DC2626" }}>
                    {r.difference > 0 ? "+" : ""}{fmt(r.difference)}
                    <AlertTriangle className="inline w-3 h-3 ml-1" />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center gap-1.5 justify-center flex-wrap">
                      <button
                        type="button"
                        onClick={() => setLedgerCustomerId(r.id)}
                        data-testid={`audit-ledger-${r.id}`}
                        className="bg-[#1E3A5F] hover:bg-[#152a47] text-white font-bold px-2.5 py-1.5 rounded-full text-[11px] flex items-center gap-1"
                        title="حركات الدين"
                      >
                        <History className="w-3 h-3" />
                        حركات
                      </button>
                      <button
                        type="button"
                        onClick={() => fix(r)}
                        data-testid={`audit-fix-${r.id}`}
                        className="bg-[#15803D] hover:bg-[#0f6630] text-white font-bold px-2.5 py-1.5 rounded-full text-[11px]"
                      >
                        تصحيح
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ledgerCustomerId && (
        <DebtLedgerModal
          customerId={ledgerCustomerId}
          onClose={() => setLedgerCustomerId(null)}
        />
      )}
    </div>
  );
};

export default DebtAuditTab;
